import { Box, Typography } from "@mui/material";
import { SearchIcon, XIcon } from "lucide-react";
import { SidebarTrigger } from "./sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Breadcrumb,
  BreadcrumbSeparator,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
} from "./breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutesEnum } from "@/routes/enum/Routes";
import { Button } from "./button";
import { SearchInput } from "../SearchInput";
import { useEffect, useState } from "react";
import { Route } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";

export function SiteHeader() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const { clienteNombre, folderId, clienteId } = state || {};
  const [openSearch, setOpenSearch] = useState(false);

  useEffect(() => {
    setOpenSearch(false);
  }, [isMobile]);

  const segments = pathname.split("/").filter(Boolean);

  const breadcrumbRoutes = {
    companies: {
      label: "Clientes",
      href: RoutesEnum.COMPANIES,
    },
    folders: {
      label: isMobile ? "Carpetas" : clienteNombre,
      href: RoutesEnum.FOLDERS,
    },
    data: {
      label: folderId,
      href: RoutesEnum.DATA_FOLDERS,
    },
    users: {
      label: "Usuarios",
      href: RoutesEnum.USERS,
    },
    feedback: {
      label: "Feedback",
      href: RoutesEnum.FEEDBACK,
    },
    updates: {
      label: "Actualizaciones",
      href: RoutesEnum.UPDATES,
    },
  };

  const renderBreadcrumbs = () => (
    <Breadcrumb>
      <BreadcrumbList>
        {segments.map((segment, index) => {
          const route =
            breadcrumbRoutes[segment as keyof typeof breadcrumbRoutes];
          if (!route) return null;

          return (
            <div key={index} className="flex items-center gap-2">
              {index > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Typography
                    fontFamily="Inter"
                    fontSize={14}
                    fontWeight={400}
                    className="text-black text-sm cursor-pointer hover:underline hover:text-[#1F6B45]"
                    sx={{ textDecoration: "none" }}
                    onClick={() =>
                      navigate(route.href, {
                        state: { folderId, clienteId, clienteNombre },
                      })
                    }
                  >
                    {route.label}
                  </Typography>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </div>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );

  const renderSearchSection = () => {
    if (!isMobile) return <SearchInput />;

    return openSearch ? (
      <Box className="flex gap-2 w-full">
        <SearchInput />
        <Button variant="icon" size="icon" onClick={() => setOpenSearch(false)}>
          <XIcon className="w-8 h-8" />
        </Button>
      </Box>
    ) : (
      <Button variant="icon" size="icon" onClick={() => setOpenSearch(true)}>
        <SearchIcon className="w-12 h-8" />
      </Button>
    );
  };

  const renderMobileRoutes = () => {
    return (
      <Box>
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-1">
            <Button variant="icon" size="icon">
              <Route />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {segments.map((segment, index) => {
              const route =
                breadcrumbRoutes[segment as keyof typeof breadcrumbRoutes];
              if (!route) return null;
              return (
                <DropdownMenuItem key={index}>
                  <Typography
                    fontFamily="Inter"
                    fontSize={14}
                    fontWeight={400}
                    className="text-black text-sm cursor-pointer hover:underline hover:text-[#1F6B45]"
                    sx={{ textDecoration: "none" }}
                    onClick={() =>
                      navigate(route.href, {
                        state: { folderId, clienteId, clienteNombre },
                      })
                    }
                  >
                    {route.label}
                  </Typography>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </Box>
    );
  };

  return (
    <header className="bg-sidebar group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 flex h-14 items-center justify-between border-b transition-[width,height]">
      <div className="flex w-full items-center justify-between px-4 lg:px-6">
        <Box className="flex items-center gap-2">
          {isMobile && (
            <SidebarTrigger
              className="-ml-1 hover:bg-gray-200 rounded-md"
              aria-label="toggle-sidebar"
            />
          )}
          {isMobile ? renderMobileRoutes() : renderBreadcrumbs()}
        </Box>
        <Box className="flex items-center justify-end gap-2">
          {renderSearchSection()}
        </Box>
      </div>
    </header>
  );
}
